/**
 * Extracts config options intended for use in `toLocaleString`.
 * @param {object} data Config object to search for options keys
 * @returns {object} Options object suitable for `toLocaleString` usage
 */
var CURRENCY = require('../constants/currency')

const decimalPrecisions = {
  PENNY: '0.01',
  NICKEL: '0.05',
  DIME: '0.1',
  HALF: '0.5',
  WHOLE_NUMBER: '1',
  FIVE: '5',
  TEN: '10',
  HUNDRED: '100',
}
function getLocaleStringOptions(data) {
  return [
    'currency',
    'minimumFractionDigits',
    'maximumFractionDigits',
    'style',
    'useGrouping',
    'currencyDisplay',
    'overridePrecision', //variable to override the decimal precision
  ].reduce(function(obj, key) {
    if (data.hasOwnProperty(key)) {
      obj[key] = data[key]
    }
    return obj
  }, {})
}

function getAbsNumber(value) {
  if (typeof value === 'string') {
    return Math.abs(value.replace(/[^\d.]/g, ''))
  } else {
    return Math.abs(value)
  }
}

/**
 * Formats a number in a locale-aware manner.
 * @param {string|number} value The number to format.
 * @param {object} config Config object
 * @param {string} config.format The format string expected by %f in __() (eg p:2;nots)
 * @param {string} [config.locale=en-US] A locale in hyphen format (eg en-US)
 * @returns {string} The formatted number
 */
function format(value, config) {
  var absNum = getAbsNumber(value)

  var absNumString = String(absNum)
  var lenNumAfterDecimal =
    absNumString.lastIndexOf('.') > -1
      ? absNumString.substring(absNumString.lastIndexOf('.') + 1).length
      : 0
  if (isNaN(absNum)) {
    console.error(
      'numerics.format requires a value that can be cast to a number, but instead received ' +
        value +
        '.'
    )
    return value
  }
  var options = getLocaleStringOptions(config)

  //Decimal Precision code start
  if (
    !options.overridePrecision &&
    options.style == 'currency' &&
    window.__LOCALE_DATA__ &&
    window.__LOCALE_DATA__.boolAllowDecimalPrecision
  ) {
    if (
      window.__LOCALE_DATA__.decimalPrecision == decimalPrecisions.WHOLE_NUMBER
    ) {
      options.minimumFractionDigits = lenNumAfterDecimal
      options.maximumFractionDigits = lenNumAfterDecimal
    }
    if (window.__LOCALE_DATA__.decimalPrecision == decimalPrecisions.PENNY) {
      options.minimumFractionDigits =
        lenNumAfterDecimal > 2 ? lenNumAfterDecimal : 2
      options.maximumFractionDigits =
        lenNumAfterDecimal > 2 ? lenNumAfterDecimal : 2
    }
  }
  // Decimal Precision code end
  var result = absNum.toLocaleString(config.locale, options)
  //replacing currency code with their corresponding symbol
  if (config.style === 'currency') {
    let currencySymbol = CURRENCY.SYMBOLS_BY_CODE[config.currency]
    result = result.replace(config.currency, currencySymbol)
    result = result.includes('\xa0') ? result.replace('\xa0', '') : result
  }
  // format negative numbers appropriately
  if (+value < 0) {
    var additions = config.negatives || ['-']
    return additions[0] + result + (additions[1] || '')
  } else {
    return result
  }
}

var optionGetters = {
  n: function(str) {
    return { negatives: str.split('') }
  },
  nots: function() {
    return { useGrouping: false }
  },
  p: function(num) {
    return {
      minimumFractionDigits: +num,
      maximumFractionDigits: +num,
    }
  },
  pmax: function(num) {
    return {
      maximumFractionDigits: +num,
    }
  },
  pmin: function(num) {
    return {
      minimumFractionDigits: +num,
      maximumFractionDigits: 20,
    }
  },
  overridePrecision: function(num) {
    return {
      overridePrecision: num.trim() == 'true' ? true : false,
    }
  },
}

/**
 * Converts the string options expected by %f in __() (eg p2;nots) to an object
 * ready for use with `number.toLocaleString`.
 * @param {string} str The format string expected by %f in __() (eg p:2;nots)
 * @returns {object} `number.toLocaleString` config object.
 */
function getOptions(str) {
  if (typeof str !== 'string' || !str) return {}
  var opts = str.split(';')

  return opts.reduce(function(obj, opt) {
    // split option into type and args (eg n:-, p:0, nots [no args])
    var data = opt.split(':')
    var getter = optionGetters[data[0].trim()]
    if (!getter) return obj
    return Object.assign(obj, getter(data[1]))
  }, {})
}

/**
 * Locale-aware formatting for any number (integer|float).
 * @param {string|number} value The number to format.
 * @param {string} formatStr The format string expected by %f in __() (eg p:2;nots)
 * @param {object} config Config object
 * @param {string} config.locale A locale in hyphen format (eg en-US)
 * @returns {string} The formatted currency
 */
function number(value, formatStr, config) {
  return format(value, Object.assign({}, getOptions(formatStr), config))
}

module.exports = {
  format: format,
  getOptions: getOptions,
  number: number,
}
